/**
 * Lectura de Excels de facturas emitidas + aplicación de un perfil de mapeo
 * para producir filas normalizadas listas para `buildIssuedSuenlaceFile`.
 */

import ExcelJS from "exceljs";
import { parseNumericLike } from "@/lib/numberFormatting";
import type {
  IssuedColumnMap,
  IssuedDefaults,
  IssuedFieldKey,
  IssuedInvoiceMapping,
} from "@/lib/issuedInvoiceMapping";
import type { IssuedInvoiceRow } from "@/lib/a3ecoExportIssued";
import { resolveGroupedTotal } from "@/lib/invoiceLineTotals";

/** Convierte "A" → 1, "B" → 2, "AA" → 27. Devuelve 0 si no es válido. */
export const columnLetterToIndex = (letter: string): number => {
  const s = (letter || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!s) return 0;
  let n = 0;
  for (let i = 0; i < s.length; i++) n = n * 26 + (s.charCodeAt(i) - 64);
  return n;
};

export const indexToColumnLetter = (idx: number): string => {
  if (idx <= 0) return "";
  let n = idx;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

export interface ParsedSheetPreview {
  sheetNames: string[];
  /** Cabeceras tal cual aparecen en la fila de cabecera. Index 0-based = columna A. */
  headers: string[];
  /** Hasta 10 filas de muestra para que el usuario verifique el mapeo. */
  sampleRows: (string | number | null)[][];
  /** Total de filas de datos en la hoja. */
  rowCount: number;
}

/**
 * Carga el primer ExcelJS Workbook desde un File. Detecta .xlsx y .xls.
 * (.csv lo deja como un único array de strings y se trata aparte si hace falta).
 */
const readWorkbook = async (file: File): Promise<ExcelJS.Workbook> => {
  const buf = await file.arrayBuffer();
  // Detectar .xls antiguo (BIFF/OLE Compound Document): D0 CF 11 E0 A1 B1 1A E1
  const head = new Uint8Array(buf.slice(0, 8));
  if (
    head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0 &&
    head[4] === 0xa1 && head[5] === 0xb1 && head[6] === 0x1a && head[7] === 0xe1
  ) {
    throw new Error(
      "El archivo es .xls (formato antiguo de Excel 97-2003). Ábrelo en Excel y guárdalo como .xlsx (Libro de Excel) antes de subirlo.",
    );
  }
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb;
};

/** Devuelve un preview (cabeceras + 10 filas de muestra) para configurar el mapeo. */
export const previewExcel = async (
  file: File,
  sheetIndex: number,
  headerRow: number,
): Promise<ParsedSheetPreview> => {
  const wb = await readWorkbook(file);
  const sheetNames = wb.worksheets.map((s) => s.name);
  const ws = wb.worksheets[Math.max(0, Math.min(sheetIndex, wb.worksheets.length - 1))];
  if (!ws) return { sheetNames, headers: [], sampleRows: [], rowCount: 0 };

  const headerRowObj = ws.getRow(headerRow);
  const headers: string[] = [];
  headerRowObj.eachCell({ includeEmpty: true }, (cell, colIdx) => {
    headers[colIdx - 1] = String(cell.value ?? "").trim();
  });

  const sampleRows: (string | number | null)[][] = [];
  const lastRow = Math.min(ws.actualRowCount, headerRow + 10);
  for (let r = headerRow + 1; r <= lastRow; r++) {
    const row = ws.getRow(r);
    const arr: (string | number | null)[] = [];
    row.eachCell({ includeEmpty: true }, (cell, colIdx) => {
      arr[colIdx - 1] = cell.value === null || cell.value === undefined ? null : String(cell.value);
    });
    if (arr.some((v) => v !== null && v !== "")) sampleRows.push(arr);
  }

  return {
    sheetNames,
    headers,
    sampleRows,
    rowCount: Math.max(0, ws.actualRowCount - headerRow),
  };
};

// ── Helpers de conversión por celda ───────────────────────────────────────

const cellToString = (raw: ExcelJS.CellValue): string => {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number") return String(raw);
  if (raw instanceof Date) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof raw === "object") {
    const obj = raw as { text?: string; result?: unknown; richText?: { text: string }[] };
    if (typeof obj.text === "string") return obj.text.trim();
    if (Array.isArray(obj.richText)) return obj.richText.map((r) => r.text).join("").trim();
    if (obj.result !== undefined) return String(obj.result).trim();
  }
  return String(raw);
};

type FormulaCellValue = ExcelJS.CellFormulaValue | (ExcelJS.CellSharedFormulaValue & { readonly formula: string });

const isFormulaCell = (raw: ExcelJS.CellValue): raw is FormulaCellValue =>
  !!raw && typeof raw === "object" && "formula" in raw && typeof (raw as FormulaCellValue).formula === "string";

const splitFormulaArgs = (args: string): string[] => {
  const out: string[] = [];
  let current = "";
  let depth = 0;
  let inQuotes = false;
  for (let i = 0; i < args.length; i++) {
    const ch = args[i];
    if (ch === '"') inQuotes = !inQuotes;
    if (!inQuotes && ch === "(") depth++;
    if (!inQuotes && ch === ")") depth--;
    if (!inQuotes && depth === 0 && (ch === "," || ch === ";")) {
      out.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) out.push(current.trim());
  return out;
};

const excelSerialToDateString = (n: number): string => {
  if (!Number.isFinite(n)) return String(n);
  const utcDays = Math.floor(n - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);
  const y = dateInfo.getUTCFullYear();
  const m = String(dateInfo.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dateInfo.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const textFormat = (value: string, format: string): string => {
  const digits = value.replace(/\D/g, "");
  if (/^0+$/.test(format) && digits) return digits.padStart(format.length, "0");
  return value;
};

const evaluateFormulaText = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  formula: string,
  visited: Set<string>,
): string | null => {
  const f = formula.replace(/^=/, "").replace(/_xlfn\./gi, "").trim();

  const evalToken = (token: string): string => {
    const t = token.trim();
    const quoted = t.match(/^"(.*)"$/);
    if (quoted) return quoted[1];
    const cellRef = t.match(/^\$?([A-Z]{1,3})\$?(\d+)$/i);
    if (cellRef) {
      const colIdx = columnLetterToIndex(cellRef[1]);
      const refRow = parseInt(cellRef[2], 10);
      const address = `${cellRef[1].toUpperCase()}${refRow}`;
      if (visited.has(address)) return "";
      visited.add(address);
      const cell = worksheet.getRow(refRow).getCell(colIdx);
      const raw = cell.value;
      // Contempla fórmulas compartidas: `raw.formula` sólo está en la base;
      // `cell.formula` es un getter que devuelve la fórmula ya traducida.
      const nestedFormula =
        (isFormulaCell(raw) && raw.formula) ||
        (cell as unknown as { formula?: string }).formula ||
        null;
      if (nestedFormula) {
        const nested = evaluateFormulaText(worksheet, refRow, nestedFormula, visited);
        if (nested !== null) return nested;
      }
      if (raw instanceof Date) return cellToString(raw);
      if (typeof raw === "number" && cell.numFmt?.toLowerCase().includes("yy")) return excelSerialToDateString(raw);
      if (cell.text && !/^\w{3}\s\w{3}\s\d{1,2}\s/i.test(cell.text)) return cell.text.trim();
      return cellToString(raw).trim();
    }
    return t;
  };

  const concat = f.match(/^(?:CONCAT|CONCATENATE)\((.*)\)$/i);
  if (concat) return splitFormulaArgs(concat[1]).map(evalToken).join("");

  const text = f.match(/^TEXT\((.*)\)$/i);
  if (text) {
    const [valueArg, formatArg] = splitFormulaArgs(text[1]);
    const format = (formatArg || "").replace(/^"|"$/g, "");
    return textFormat(evalToken(valueArg), format);
  }

  if (f.includes("&")) return splitFormulaArgs(f.replace(/&/g, ",")).map(evalToken).join("");

  // Si la fórmula es una referencia simple, devolver el texto de esa celda.
  if (/^\$?[A-Z]{1,3}\$?\d+$/i.test(f)) return evalToken(f);

  return null;
};

const parseDate = (raw: ExcelJS.CellValue, fmt: IssuedDefaults["dateFormat"]): string => {
  if (raw instanceof Date) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = cellToString(raw);
  if (!s) return "";
  // ISO
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  // dd/mm/yyyy o mm/dd/yyyy
  const slash = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (slash) {
    let [, a, b, y] = slash;
    if (y.length === 2) y = (parseInt(y) >= 70 ? "19" : "20") + y;
    const useMdy = fmt === "mm/dd/yyyy";
    const dd = useMdy ? b : a;
    const mm = useMdy ? a : b;
    return `${y}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  return s;
};

const parseNumber = (raw: ExcelJS.CellValue, sep: IssuedDefaults["decimalSeparator"]): number => {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return raw;
  let s = cellToString(raw);
  if (!s) return 0;
  // Quita símbolo €, espacios, NBSP
  s = s.replace(/€/g, "").replace(/\u00a0/g, "").replace(/\s+/g, "").trim();
  if (sep === ",") {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (sep === ".") {
    s = s.replace(/,/g, "");
  } else {
    // auto: si hay ambos, el último separador es el decimal
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else if (lastDot > lastComma) {
      s = s.replace(/,/g, "");
    }
  }
  return parseNumericLike(s);
};

// ── Import principal ──────────────────────────────────────────────────────

export interface ImportResult {
  rows: IssuedInvoiceRow[];
  /** Errores no bloqueantes (filas saltadas, valores inválidos, etc.). */
  notes: string[];
}

const getCellValue = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  field: IssuedFieldKey,
  columnMap: IssuedColumnMap,
): ExcelJS.CellValue => {
  const letter = columnMap[field];
  if (!letter) return null;
  const colIdx = columnLetterToIndex(letter);
  if (colIdx <= 0) return null;
  return worksheet.getRow(rowIndex).getCell(colIdx).value;
};

/**
 * Igual que `getCellValue` pero devuelve el TEXTO tal como se ve en la celda.
 * Imprescindible para cuentas contables numéricas con ceros a la izquierda
 * (43000001 vs "043000001"), códigos postales, series alfanuméricas…
 */
const getCellFormula = (cell: ExcelJS.Cell): string | null => {
  const raw = cell.value;
  if (isFormulaCell(raw) && raw.formula) return raw.formula;
  // ExcelJS guarda `sharedFormula` en las celdas hijas y expone `cell.formula`
  // como getter ya traducido con la fórmula base compartida.
  const translated = (cell as unknown as { formula?: string }).formula;
  if (typeof translated === "string" && translated.trim()) return translated;
  return null;
};

const getCellText = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  field: IssuedFieldKey,
  columnMap: IssuedColumnMap,
): string => {
  const letter = columnMap[field];
  if (!letter) return "";
  const colIdx = columnLetterToIndex(letter);
  if (colIdx <= 0) return "";
  const cell = worksheet.getRow(rowIndex).getCell(colIdx);
  const raw = cell.value;
  const formula = getCellFormula(cell);
  if (formula) {
    const evaluated = evaluateFormulaText(worksheet, rowIndex, formula, new Set([cell.address]));
    if (evaluated !== null && evaluated.trim()) return evaluated.trim();
  }
  const asText = (cell.text ?? "").toString().trim();
  // Excel aplica máscara de fecha a valores no-fecha → "Invalid Date"; ignorar.
  if (asText && asText !== "Invalid Date") return asText;
  return cellToString(raw);
};


export const importIssuedExcel = async (
  file: File,
  mapping: IssuedInvoiceMapping,
): Promise<ImportResult> => {
  const wb = await readWorkbook(file);
  const ws = wb.worksheets[Math.max(0, Math.min(mapping.sheet_index, wb.worksheets.length - 1))];
  if (!ws) return { rows: [], notes: ["No se pudo abrir la hoja seleccionada."] };

  const notes: string[] = [];
  const rows: IssuedInvoiceRow[] = [];
  const defaults = mapping.defaults || {};
  const map = mapping.column_map || {};

  const startRow = mapping.header_row + 1;
  const endRow = ws.actualRowCount;

  for (let r = startRow; r <= endRow; r++) {
    const row = ws.getRow(r);
    const isEmpty = !row.hasValues;
    if (isEmpty) continue;

    const invoiceNumberRaw = getCellText(ws, r, "invoiceNumber", map);
    if (!invoiceNumberRaw) {
      // Línea vacía o total/subtotal — la saltamos en silencio.
      continue;
    }

    const date = parseDate(getCellValue(ws, r, "invoiceDate", map), defaults.dateFormat || "auto");
    if (!date) {
      notes.push(`Fila ${r}: factura ${invoiceNumberRaw} sin fecha válida — se omite.`);
      continue;
    }

    const series = getCellText(ws, r, "series", map);
    const customerName = getCellText(ws, r, "customerName", map);
    const customerTaxId = getCellText(ws, r, "customerTaxId", map)
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, "");
    // Cuenta de cliente: LEER SIEMPRE COMO TEXTO para preservar ceros a la
    // izquierda. Sólo caemos al default del perfil si la celda está vacía.
    const customerAccountRaw = getCellText(ws, r, "customerAccount", map);
    const customerAccount = customerAccountRaw || defaults.defaultCustomerAccount || "";
    if (!customerAccountRaw && !defaults.defaultCustomerAccount) {
      notes.push(
        `Fila ${r}: factura ${invoiceNumberRaw} sin cuenta de cliente (columna vacía y sin cuenta por defecto).`,
      );
    }
    const salesAccountRaw = getCellText(ws, r, "salesAccount", map);
    const salesAccount = salesAccountRaw || defaults.defaultSalesAccount || "";


    const sep = defaults.decimalSeparator || "auto";
    const taxableBase = parseNumber(getCellValue(ws, r, "taxableBase", map), sep);
    const vatAmount = parseNumber(getCellValue(ws, r, "vatAmount", map), sep);
    const totalAmount = parseNumber(getCellValue(ws, r, "totalAmount", map), sep);
    const irpfAmount = parseNumber(getCellValue(ws, r, "irpfAmount", map), sep);
    const reAmount = parseNumber(getCellValue(ws, r, "reAmount", map), sep);

    let vatRate = parseNumber(getCellValue(ws, r, "vatRate", map), sep);
    if (!vatRate && taxableBase) {
      // Inferir tipo IVA aproximando al más cercano de 0/4/10/21.
      const computed = (vatAmount / taxableBase) * 100;
      const candidates = [0, 4, 10, 21];
      vatRate = candidates.reduce((best, c) =>
        Math.abs(c - computed) < Math.abs(best - computed) ? c : best, 0);
    }
    let reRate = parseNumber(getCellValue(ws, r, "reRate", map), sep);
    if (!reRate && reAmount && taxableBase) {
      const computed = (reAmount / taxableBase) * 100;
      const candidates = [0, 0.5, 1.4, 5.2];
      reRate = candidates.reduce((best, c) =>
        Math.abs(c - computed) < Math.abs(best - computed) ? c : best, 0);
    }
    let irpfRate = parseNumber(getCellValue(ws, r, "irpfRate", map), sep);
    if (!irpfRate && irpfAmount && taxableBase) {
      irpfRate = Math.round((Math.abs(irpfAmount) / Math.abs(taxableBase)) * 10000) / 100;
    }

    const postalCode = getCellText(ws, r, "postalCode", map);
    const paymentMethod = getCellText(ws, r, "paymentMethod", map);

    rows.push({
      date,
      series: series || undefined,
      invoiceNumber: invoiceNumberRaw,
      customerName,
      customerTaxId,
      customerAccount,
      salesAccount,
      vatLines: [{ taxableBase, vatRate, vatAmount, reRate: reRate || undefined, reAmount: reAmount || undefined }],
      irpfRate: irpfRate || null,
      irpfAmount: irpfAmount || null,
      totalAmount,
      postalCode: postalCode || undefined,
      paymentMethod: paymentMethod || undefined,
    });
  }

  // Agrupar filas con misma serie+nº de factura+fecha en una factura multilínea
  // (necesario para ERPs que exportan una fila por tipo de IVA).
  const grouped = new Map<string, { row: IssuedInvoiceRow; totals: number[] }>();
  for (const row of rows) {
    const key = `${row.series ?? ""}|${row.invoiceNumber}|${row.date}|${row.customerTaxId}`;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { row: { ...row, vatLines: [...row.vatLines] }, totals: [row.totalAmount] });
    } else {
      existing.row.vatLines.push(...row.vatLines);
      existing.totals.push(row.totalAmount);
      // IRPF viene repetido en cada línea del ERP — no acumular.
      if (!existing.row.irpfAmount && row.irpfAmount) existing.row.irpfAmount = row.irpfAmount;
      if (!existing.row.irpfRate && row.irpfRate) existing.row.irpfRate = row.irpfRate;
    }
  }

  // El total puede venir repetido (global) o por línea según el ERP:
  // resolveGroupedTotal detecta la convención y acumula sólo si procede.
  const finalRows = Array.from(grouped.values()).map((g) =>
    resolveGroupedTotal(g.row, g.totals),
  );

  return { rows: finalRows, notes };
};
